import Phaser from 'phaser';
import { BootScene } from './scenes/BootScene';
import { MainMenuScene } from './scenes/MainMenuScene';
import { GameScene } from './scenes/GameScene';
import { ProtocellScene } from './scenes/ProtocellScene';
import { CytoskeletonScene } from './scenes/CytoskeletonScene';
import { SettingsScene } from './scenes/SettingsScene';
import { LobbyScene } from './scenes/LobbyScene';
import { MultiplayerGameScene } from './scenes/MultiplayerGameScene';
import { debounce } from './utils/helpers';

// Try to load saved FPS setting from localStorage
function getSavedFps(): number {
  try {
    const savedSettings = localStorage.getItem('cytopodSettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      if (settings.fps && settings.fps >= 30 && settings.fps <= 120) {
        return settings.fps;
      }
    }
  } catch (error) {
    console.error('Error loading saved FPS setting:', error);
  }
  
  // Default to 60 if no saved setting or error
  return 60;
}

// Game configuration
const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  width: window.innerWidth,
  height: window.innerHeight,
  physics: {
    default: 'arcade',
    arcade: {
      gravity: { y: 0, x: 0 },
      debug: false
    }
  },
  scene: [
    BootScene, 
    MainMenuScene, 
    ProtocellScene, 
    CytoskeletonScene, 
    GameScene, 
    SettingsScene,
    LobbyScene,
    MultiplayerGameScene
  ],
  backgroundColor: '#0a2a3a', // Dark blue-green color
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    parent: 'game-container',
    width: window.innerWidth,
    height: window.innerHeight,
    min: {
      width: 320,
      height: 240
    },
    max: {
      width: 4096,
      height: 2160
    },
    expandParent: true,
    fullscreenTarget: 'game-container'
  },
  transparent: false,
  roundPixels: true,
  render: {
    antialias: true,
    pixelArt: false,
    roundPixels: true
  },
  dom: {
    createContainer: true
  },
  disableContextMenu: true,
  fps: {
    target: getSavedFps(),   // Use saved FPS setting or default
    min: 30,      // Minimum acceptable frame rate
    forceSetTimeOut: false,  // Use setTimeout instead of requestAnimationFrame
    deltaHistory: 10         // How many frames to use for delta average calculation
  }
};

// Apply styles to ensure the game container fills the screen
function applyFullscreenStyles() {
  // Make sure the game container fills the browser window
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) {
    gameContainer.style.width = '100vw';
    gameContainer.style.height = '100vh';
    gameContainer.style.margin = '0';
    gameContainer.style.padding = '0';
    gameContainer.style.overflow = 'hidden';
    gameContainer.style.position = 'absolute';
    gameContainer.style.top = '0';
    gameContainer.style.left = '0';
  }
  
  // Apply full screen styles to body and html
  document.body.style.margin = '0';
  document.body.style.padding = '0';
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed'; // Prevent scrolling on mobile
  document.body.style.width = '100%';
  document.body.style.height = '100%';
  document.documentElement.style.width = '100%';
  document.documentElement.style.height = '100%';
  document.documentElement.style.overflow = 'hidden';
}

// Remove loading text when game starts
window.addEventListener('load', () => {
  console.log('Window loaded, initializing game with proper size');
  const loadingElement = document.getElementById('loading');
  if (loadingElement && loadingElement.parentNode) {
    loadingElement.parentNode.removeChild(loadingElement);
  }
  
  // Apply fullscreen styles
  applyFullscreenStyles();
});

// Initialize the game
let game: Phaser.Game;
window.addEventListener('load', () => {
  console.log('Setting up game with window size:', window.innerWidth, window.innerHeight);
  
  // Update config with current window size
  config.width = window.innerWidth;
  config.height = window.innerHeight;
  
  // Short delay to ensure DOM is fully loaded and sized correctly
  setTimeout(() => {
    game = new Phaser.Game(config);
    
    // Create a debounced resize handler to prevent too many resize events
    const handleResize = debounce(() => {
      console.log('Window resized to:', window.innerWidth, window.innerHeight);
      
      if (game) {
        try {
          // Apply fullscreen styles again on resize
          applyFullscreenStyles();
          
          // Update game size
          game.scale.resize(window.innerWidth, window.innerHeight);
          
          // Force a refresh of the scale manager
          game.scale.refresh();
          
          // Make sure the background color is maintained
          const canvas = document.querySelector('canvas');
          if (canvas) {
            console.log('Found canvas element, ensuring it remains visible');
            canvas.style.display = 'block';
            // Ensure the canvas background color is set (matches the config)
            canvas.style.backgroundColor = '#0a2a3a';
          } else {
            console.warn('Canvas element not found during resize!');
          }
          
          // Get active scenes
          const activeScenes = game.scene.getScenes(true);
          console.log(`Updating ${activeScenes.length} active scenes`);
          
          // Update each scene's cameras safely
          activeScenes.forEach(scene => {
            try {
              console.log(`Updating scene: ${scene.scene.key}`);
              
              // Update all cameras in the scene
              if (scene.cameras && scene.cameras.cameras) {
                scene.cameras.cameras.forEach(camera => {
                  try {
                    console.log(`Updating camera: ${camera.name || 'unnamed'}`);
                    
                    if (camera.name !== 'uiCam') { // Skip UI cameras
                      // Recenter the camera on its current center point
                      const centerX = camera.midPoint ? camera.midPoint.x : camera.centerX;
                      const centerY = camera.midPoint ? camera.midPoint.y : camera.centerY;
                      camera.centerOn(centerX, centerY);
                      console.log(`Recentered camera at: ${centerX}, ${centerY}`);
                    } else {
                      // For UI cameras, just reset size and position
                      camera.setSize(window.innerWidth, window.innerHeight);
                      camera.setPosition(0, 0);
                      console.log('Reset UI camera size and position');
                    }
                  } catch (cameraError) {
                    console.error(`Error updating camera in ${scene.scene.key}:`, cameraError);
                  }
                });
              }
              
              // Emit a custom event that scenes can listen for
              try {
                // Special handling for LobbyScene which can be particularly sensitive to resize
                if (scene.scene.key === 'LobbyScene') {
                  console.log('Special handling for LobbyScene during resize');
                  // Give LobbyScene a chance to initialize properly before handling resize
                  setTimeout(() => {
                    try {
                      scene.events.emit('windowResized', {
                        width: window.innerWidth, 
                        height: window.innerHeight
                      });
                      console.log('Emitted delayed windowResized event to LobbyScene');
                    } catch (lobbyError) {
                      console.error('Error handling delayed resize for LobbyScene:', lobbyError);
                    }
                  }, 300);
                } else {
                  // Normal handling for other scenes
                  scene.events.emit('windowResized', {
                    width: window.innerWidth, 
                    height: window.innerHeight
                  });
                  console.log(`Emitted windowResized event to scene: ${scene.scene.key}`);
                }
              } catch (eventError) {
                console.error(`Error emitting resize event to ${scene.scene.key}:`, eventError);
              }
            } catch (sceneError) {
              console.error(`Error handling resize for scene ${scene.scene.key}:`, sceneError);
            }
          });
          
          console.log('Game resized and cameras updated');
        } catch (error) {
          console.error('Error during resize handling:', error);
        }
      }
    }, 250); // Debounce by 250ms to avoid excessive updates
    
    // Add resize event listener to update the game size
    window.addEventListener('resize', handleResize);
    
    // Add orientation change event listener for mobile devices
    window.addEventListener('orientationchange', () => {
      console.log('Orientation changed, updating game size after delay');
      // Use a longer delay for orientation changes as they take longer to complete
      setTimeout(handleResize, 500);
    });
  }, 100);
});

// Export the config for use in other files
export default config; 